import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ROLE_AR    = { company_admin: 'مدير شركة', driver: 'سائق', bus_host: 'مضيف البص' };
const ROLE_COLOR = { company_admin: 'bg-blue-100 text-blue-700', driver: 'bg-purple-100 text-purple-700', bus_host: 'bg-teal-100 text-teal-700' };

const StaffPage = () => {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]       = useState({ name: '', email: '', phone: '', password: '', role: 'driver' });
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving]   = useState(false);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/staff');
      setStaff(res.data.data);
    } catch { setStaff([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStaff(); }, []);

  const set = (f) => (e) => setForm((prev) => ({ ...prev, [f]: e.target.value }));

  const createUser = async (e) => {
    e.preventDefault();
    setFormErr('');
    if (form.password.length < 6) { setFormErr('كلمة المرور 6 أحرف على الأقل'); return; }
    setSaving(true);
    try {
      await api.post('/users/staff', form);
      setForm({ name: '', email: '', phone: '', password: '', role: 'driver' });
      setShowForm(false);
      fetchStaff();
    } catch (e) {
      setFormErr(e.response?.data?.message || 'خطأ في إنشاء المستخدم');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (id, isActive) => {
    try {
      await api.patch(`/users/staff/${id}/toggle`);
      setStaff((s) => s.map((u) => u.id === id ? { ...u, isActive: !u.isActive } : u));
    } catch (e) {
      alert(e.response?.data?.message || 'خطأ');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">إدارة فريق العمل</h1>
          <p className="text-gray-500 text-sm">السائقون ومديرو الشركة</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="bg-blue-700 hover:bg-blue-800 text-white px-4 md:px-5 py-2 md:py-2.5 rounded-lg font-semibold text-sm transition whitespace-nowrap">
          {showForm ? '← إلغاء' : '+ إضافة مستخدم'}
        </button>
      </div>

      {/* Add User Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="font-bold text-gray-700 mb-4">إضافة مستخدم جديد</h2>
          {formErr && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 mb-4 text-sm">{formErr}</div>}
          <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['name', 'الاسم الكامل', 'text', 'محمد أحمد'],
              ['email', 'البريد الإلكتروني', 'email', 'user@company.com'],
              ['phone', 'رقم الهاتف', 'tel', '0500000000'],
              ['password', 'كلمة المرور', 'password', '6 أحرف على الأقل'],
            ].map(([field, label, type, placeholder]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label} *</label>
                <input type={type} placeholder={placeholder} required value={form[field]} onChange={set(field)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الدور *</label>
              <select value={form.role} onChange={set('role')}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="driver">سائق</option>
                <option value="bus_host">مضيف البص</option>
                <option value="company_admin">مدير شركة</option>
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={saving}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-8 py-2.5 rounded-lg font-semibold text-sm">
                {saving ? 'جاري الحفظ...' : 'حفظ المستخدم'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Staff List */}
      {loading ? (
        <div className="text-center py-20 text-gray-400">جاري التحميل...</div>
      ) : staff.length === 0 ? (
        <div className="text-center bg-white rounded-xl shadow py-16 text-gray-400">
          <div className="text-5xl mb-3">👤</div>
          <p>لا يوجد أعضاء في فريق العمل بعد.</p>
          <p className="text-sm mt-1">ابدأ بإضافة سائق أو مدير.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-right px-4 py-3 text-gray-600 font-semibold">الاسم</th>
                <th className="text-right px-4 py-3 text-gray-600 font-semibold">البريد الإلكتروني</th>
                <th className="text-right px-4 py-3 text-gray-600 font-semibold">الهاتف</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الدور</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">الحالة</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">إجراء</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {staff.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3 text-gray-500">{u.phone || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${ROLE_COLOR[u.role]}`}>
                      {ROLE_AR[u.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {u.isActive ? 'نشط' : 'موقوف'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleStatus(u.id, u.isActive)}
                      className={`text-xs px-3 py-1 rounded-lg ${u.isActive ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                      {u.isActive ? 'إيقاف' : 'تفعيل'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPage;
